import { Collision } from '@dnd-kit/core';
import dayjs from 'dayjs';
import { SnackbarVariant } from '@eas/common-web';
import {
  CalendarDataSourceType,
  CalendarHandle,
} from '@modules/planner/calendar/calendar-types';
import {
  addStartingPointCoords,
  stringifyCoords,
} from '@modules/planner/unit-menu/utils';
import {
  planWorkOrder,
  updateTravelTime,
} from '@modules/work-order/work-order-api';
import { getDrivingInfo } from '@composite/map/utils/get-driving-info';
import { GPSPoint, WorkOrder } from '@models';
import {
  planWorkOrderErrorCodesEnum,
  snackbarVariantsEnum,
} from '../utils/plan-single-work-order';
import { prepareDto } from '../utils/prepare-dto';
import { dragEndUtils } from './drag-end-utils';
import { useValidateWorkOrder } from './validate';

type Props = {
  showSnackbar: (message: string, variant: SnackbarVariant) => void;
  workOrders: WorkOrder[];
  calendarRef: React.RefObject<CalendarHandle>;
  collision: Collision;
  nextWorkOrder?: WorkOrder;
  prevWorkOrder?: WorkOrder;
  unitStartingPoint: GPSPoint;
  calendarSource: CalendarDataSourceType;
  unitId: string;
  validate: ReturnType<typeof useValidateWorkOrder>['validate'];
};

export const handlePlanMultipleWorkOrders = async ({
  showSnackbar,
  workOrders,
  calendarRef,
  calendarSource,
  collision,
  nextWorkOrder,
  prevWorkOrder,
  unitStartingPoint,
  unitId,
  validate,
}: Props) => {
  let travelTimeAcc = 0;
  const calendarStartDate = dayjs.unix(calendarRef.current?.currentDate ?? 0);
  const unitIndex = (calendarSource?.yAxis?.data ?? []).findIndex(
    (row) => row.id === unitId
  );
  for (let i = 0; i < workOrders.length; i++) {
    const newWorkOrderTravel = {
      previous: 0,
      next: 0,
    };
    try {
      //let coordsString = '';
      if (!prevWorkOrder) {
        let coordsString = stringifyCoords([workOrders[i]]);
        coordsString = addStartingPointCoords(
          coordsString,
          unitStartingPoint,
          'start'
        );
        const travelTime = await getDrivingInfo(coordsString);
        newWorkOrderTravel.previous = travelTime.duration;
      } else {
        const coordsString = stringifyCoords([prevWorkOrder, workOrders[i]]);
        const travelTime = await getDrivingInfo(coordsString);
        newWorkOrderTravel.previous = travelTime.duration;
      }

      // skip for 1st work order
      if (i !== 0) {
        travelTimeAcc += Math.round(newWorkOrderTravel.previous);
      }

      const newWorkOrder = prepareDto({
        currentDate: calendarRef.current?.currentDate ?? 0,
        collision,
        workOrder: workOrders[i],
        offset: travelTimeAcc,
      });

      if (!newWorkOrder) {
        showSnackbar(
          `PP ${workOrders[i]?.workOrderId} sa nepodarilo pripraviť na naplánovanie.`,
          SnackbarVariant.ERROR
        );
        // if we are on first PP, we dont need to refetch (return false), otherwise refetch
        return i === 0 ? false : true;
      }

      const isMultiDay = dragEndUtils.getIsMultiday({
        workOrder: newWorkOrder,
        calendarStartDate: calendarStartDate
          .startOf('day')
          .format('YYYY-MM-DD'),
        shifts: calendarSource?.yAxis?.data?.[unitIndex].shifts ?? [],
      });

      const result = await validate(newWorkOrder, isMultiDay, true);

      if (result.ok === false && !result.allOk) {
        // if we are on first PP, we dont need to refetch (return false), otherwise refetch
        return i === 0 ? false : true;
      }

      // Naplanuj aktualny PP

      const plannedWorOrderResponse = (await planWorkOrder(
        newWorkOrder.id,
        {
          ownerType: newWorkOrder.unitType,
          ownerId: newWorkOrder.unitId,
          plannedFrom: newWorkOrder.plannedFrom,
          plannedTo: newWorkOrder.plannedTo,
        },
        isMultiDay
      ).json()) as WorkOrder;

      const { startTime, endTime } = plannedWorOrderResponse;

      // we cant use work order's duration, because endTime - startTime != duration in multiday work orders
      travelTimeAcc += dayjs(endTime).diff(dayjs(startTime), 'second');

      ////////////////////////////////////////
      /////// UPDATE TRAVEL TIMES ////////////
      ////////////////////////////////////////

      if (i === workOrders.length - 1) {
        if (
          !nextWorkOrder ||
          dayjs(newWorkOrder.plannedTo).isAfter(dayjs(nextWorkOrder.endTime))
        ) {
          // Som na poslednom PP z vybranych DnD, zaroven exituje nejake next PP
          // a zaroven exitujuci PP konci driv nez tie co som dropol
          // musim novemu poslednemu nastavit travelNext a tomu exitujucemu travelNext = 0

          let coordsTravelNext = stringifyCoords([newWorkOrder]);
          coordsTravelNext = addStartingPointCoords(
            coordsTravelNext,
            unitStartingPoint,
            'end'
          );

          const travelTimeNext = await getDrivingInfo(coordsTravelNext);
          await updateTravelTime(newWorkOrder.id, {
            travelNext: Math.round(travelTimeNext.duration),
            travelPrevious: Math.round(newWorkOrderTravel.previous),
          }).json();

          if (nextWorkOrder) {
            // updatni povodny posledny PP ak exituje
            await updateTravelTime(nextWorkOrder.id, {
              travelNext: 0,
              travelPrevious: nextWorkOrder.travelPrevious ?? 0,
            }).json();
          }
        } else {
          // Som na poslednom PP z vybranych DnD, zaroven exituje nejake next PP,
          // ale zaroven exitujuci next PP nekonci driv nez tie co som dropol
          await updateTravelTime(newWorkOrder.id, {
            travelNext: 0,
            travelPrevious: Math.round(newWorkOrderTravel.previous),
          }).json();

          // Pripad ze som na poslednom PP a zaroven ten konci pred tym nez povodny PP
          const coords = stringifyCoords([newWorkOrder, nextWorkOrder]);
          const travelTimeLastPrevious = await getDrivingInfo(coords);
          // updatni povodny posledny PP
          await updateTravelTime(nextWorkOrder.id, {
            travelNext: nextWorkOrder.travelNext ?? 0,
            travelPrevious: Math.round(travelTimeLastPrevious.duration),
          }).json();
        }
      } else {
        // nie som na poslednom PP z vybranych DnD
        await updateTravelTime(newWorkOrder.id, {
          travelNext: 0,
          travelPrevious: Math.round(newWorkOrderTravel.previous),
        }).json();
      }
      prevWorkOrder = workOrders[i];
    } catch (error) {
      console.error(error);
      const code = (error as { code: string })?.code ?? 'DEFAULT';
      const errorMsg = planWorkOrderErrorCodesEnum[code];
      const snackbarVariant =
        snackbarVariantsEnum[code] ?? SnackbarVariant.ERROR;

      showSnackbar(errorMsg, snackbarVariant);
      return true;
    }
  }
  return true;
};
